"use client";

import {
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { IconComponent } from "@/lib/icon-context";
import { cn } from "@/lib/utils";
import { useShape } from "@/lib/shape-context";
import { useFluidHoverGroupItem } from "@/components/ui/fluid-hover-group";

const buttonVariants = cva(
  [
    "group/btn relative isolate inline-flex items-center justify-center whitespace-nowrap font-medium outline-none cursor-pointer select-none",
    "[&_svg]:shrink-0 [&_svg]:pointer-events-none [&_svg:not([data-spinner])]:[stroke-width:var(--icon-stroke)] [&_svg:not([data-spinner])]:transition-[stroke-width] [&_svg:not([data-spinner])]:duration-80 group-hover/btn:[&_svg:not([data-spinner])]:[stroke-width:calc(var(--icon-stroke)_+_0.25)]",
    "transition-colors duration-80",
    "disabled:opacity-50 disabled:pointer-events-none",
    "focus-visible:ring-1 focus-visible:ring-[color:var(--focus-ring,#6B97FF)]",
  ],
  {
    variants: {
      variant: {
        primary: "text-background",
        secondary: "text-foreground",
        tertiary: "text-foreground",
        ghost: "text-muted-foreground hover:text-foreground",
        bare: "text-muted-foreground hover:text-foreground",
        destructive: "text-destructive",
        "destructive-solid": "text-destructive-foreground",
        "ghost-destructive": "text-muted-foreground hover:text-destructive",
      },
      size: {
        default: "h-9 px-4 text-[length:var(--fs-subtitle)] leading-[var(--lh-subtitle)] gap-1.5 [&_svg:not([data-spinner])]:size-4",
        compact: "h-7 px-3 text-[length:var(--fs-body-compact)] leading-[var(--lh-body-compact)] gap-1 [&_svg:not([data-spinner])]:size-3.5",
        medium: "h-10 px-4 text-[length:var(--fs-title-compact)] leading-[var(--lh-title-compact)] gap-1.5 [&_svg:not([data-spinner])]:size-4",
        large: "h-11 px-5 text-[length:var(--fs-title)] leading-[var(--lh-title)] gap-2 [&_svg:not([data-spinner])]:size-5",
        "icon-sm": "h-8 w-8 p-0 [&_svg:not([data-spinner])]:size-4",
        icon: "h-9 w-9 p-0 [&_svg:not([data-spinner])]:size-5",
        "icon-compact": "h-7 w-7 p-0 [&_svg:not([data-spinner])]:size-3.5",
      },
      iconLeft: { true: "" },
      iconRight: { true: "" },
    },
    compoundVariants: [
      { size: "compact", iconLeft: true, className: "pl-2" },
      { size: "default", iconLeft: true, className: "pl-3" },
      { size: "medium", iconLeft: true, className: "pl-3" },
      { size: "large", iconLeft: true, className: "pl-4" },
      { size: "compact", iconRight: true, className: "pr-2" },
      { size: "default", iconRight: true, className: "pr-3" },
      { size: "medium", iconRight: true, className: "pr-3" },
      { size: "large", iconRight: true, className: "pr-4" },
    ],
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

type ButtonSizeCanonical =
  | "default"
  | "compact"
  | "medium"
  | "large"
  | "icon"
  | "icon-sm"
  | "icon-compact";

type ButtonSize =
  | ButtonSizeCanonical
  | "sm"
  | "md"
  | "lg"
  | "icon-lg";

const legacySizeAliases: Partial<Record<ButtonSize, ButtonSizeCanonical>> = {
  sm: "compact",
  md: "default",
  lg: "default",
  "icon-lg": "icon",
};

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, "size"> {
  size?: ButtonSize;
  pill?: boolean;
  asChild?: boolean;
  render?: ReactElement;
  nativeButton?: boolean;
  loading?: boolean;
  leadingIcon?: IconComponent;
  trailingIcon?: IconComponent;
  active?: boolean;
}

const bgVariants: Record<string, string> = {
  "destructive-solid":
    "[--btn-bg:hsl(var(--destructive))] group-hover/btn:[--btn-bg:hsl(var(--destructive)/0.9)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)]",
  primary:
    "[--btn-bg:hsl(var(--foreground))] group-hover/btn:[--btn-bg:color-mix(in_oklab,hsl(var(--foreground))_90%,hsl(var(--background)))] group-active/btn:[--btn-bg:color-mix(in_oklab,hsl(var(--foreground))_80%,hsl(var(--background)))] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  secondary:
    "[--btn-bg:var(--tint)] group-hover/btn:[--btn-bg:var(--tint-hover)] group-active/btn:[--btn-bg:var(--tint)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  tertiary:
    "bg-transparent shadow-[0_0_0_1px_hsl(var(--border)),inset_0_0_0_0px_hsl(var(--border))] group-hover/btn:bg-hover group-active/btn:bg-active group-active/btn:shadow-[0_0_0_0px_hsl(var(--border)),inset_0_0_0_1px_hsl(var(--border))]",
  destructive:
    "[--btn-bg:hsl(var(--destructive)/0.1)] group-hover/btn:[--btn-bg:hsl(var(--destructive)/0.2)] group-active/btn:[--btn-bg:hsl(var(--destructive)/0.1)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  bare: "bg-transparent",
  "ghost-destructive":
    "[--btn-bg:transparent] group-hover/btn:[--btn-bg:hsl(var(--destructive)/0.1)] group-active/btn:[--btn-bg:hsl(var(--destructive)/0.15)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  ghost:
    "bg-transparent shadow-[0_0_0_1px_transparent] group-hover/btn:bg-hover group-hover/btn:shadow-[0_0_0_1px_var(--hover)] group-active/btn:bg-active group-active/btn:shadow-[0_0_0_0px_var(--active)]",
};

const activeBgVariants: Record<string, string> = {
  "destructive-solid":
    "[--btn-bg:hsl(var(--destructive))] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)]",
  primary:
    "[--btn-bg:color-mix(in_oklab,hsl(var(--foreground))_80%,hsl(var(--background)))] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  secondary:
    "[--btn-bg:var(--tint)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  tertiary:
    "bg-active shadow-[0_0_0_1px_hsl(var(--border)),inset_0_0_0_0px_hsl(var(--border))] group-active/btn:shadow-[0_0_0_0px_hsl(var(--border)),inset_0_0_0_1px_hsl(var(--border))]",
  destructive:
    "[--btn-bg:hsl(var(--destructive)/0.2)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  bare: "bg-active",
  "ghost-destructive":
    "[--btn-bg:hsl(var(--destructive)/0.15)] bg-[var(--btn-bg)] shadow-[0_0_0_1px_var(--btn-bg)] group-active/btn:shadow-[0_0_0_0px_var(--btn-bg)]",
  ghost:
    "bg-active shadow-[0_0_0_1px_var(--active)] group-active/btn:shadow-[0_0_0_0px_var(--active)]",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      render,
      nativeButton: _nativeButton,
      loading = false,
      leadingIcon: LeadingIcon,
      trailingIcon: TrailingIcon,
      active = false,
      pill = false,
      disabled,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const asChildElement =
      render && isValidElement(render)
        ? children === undefined
          ? (render as ReactElement<{ children?: ReactNode }>)
          : cloneElement(render as ReactElement<{ children?: ReactNode }>, undefined, children)
        : asChild && isValidElement(children)
          ? (children as ReactElement<{ children?: ReactNode }>)
          : null;
    const Comp = asChildElement ? Slot : "button";
    const itemRef = useFluidHoverGroupItem<HTMLButtonElement>(ref, variant === "bare");
    const label = asChildElement ? asChildElement.props.children : children;
    const resolvedSize: ButtonSizeCanonical = size
      ? legacySizeAliases[size] ?? (size as ButtonSizeCanonical)
      : "default";
    const isIconOnly =
      resolvedSize === "icon" ||
      resolvedSize === "icon-sm" ||
      resolvedSize === "icon-compact";
    const isCompact =
      resolvedSize === "compact" || resolvedSize === "icon-compact";
    const isLarge = resolvedSize === "large";
    const iconSize = isCompact ? "0.875rem" : isLarge ? "1.25rem" : "1rem";
    const spinnerSizeClass = isCompact
      ? "h-7 w-7"
      : isLarge
        ? "h-11 w-11"
        : resolvedSize === "medium"
          ? "h-10 w-10"
          : "h-9 w-9";
    const shape = useShape();
    const bgClass = active
      ? activeBgVariants[variant ?? "primary"]
      : bgVariants[variant ?? "primary"];

    const internals = (
      <>
        <span
          aria-hidden
          className={cn(
            "absolute inset-px rounded-[inherit] transition-[box-shadow,background-color] [transition-duration:180ms,80ms] [transition-timing-function:cubic-bezier(0.23,1,0.32,1),ease] group-active/btn:[transition-duration:80ms,80ms]",
            bgClass
          )}
        />
        <span className="relative inline-flex w-full min-w-0 items-center [justify-content:inherit] [gap:inherit]">
          {loading ? (
            <>
              <span className="flex items-center justify-center [gap:inherit] opacity-0">
                {LeadingIcon && !isIconOnly && (
                  <LeadingIcon size={iconSize} strokeWidth={2} />
                )}
                {label}
                {TrailingIcon && !isIconOnly && (
                  <TrailingIcon size={iconSize} strokeWidth={2} />
                )}
              </span>
              <span className="absolute inset-0 flex items-center justify-center">
                <svg
                  data-spinner
                  className={spinnerSizeClass}
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M 12 12 C 14 8.5 19 8.5 19 12 C 19 15.5 14 15.5 12 12 C 10 8.5 5 8.5 5 12 C 5 15.5 10 15.5 12 12 Z"
                    stroke="currentColor"
                    strokeWidth="1.125"
                    strokeLinecap="round"
                    pathLength="100"
                    style={{
                      strokeDasharray: "15 85",
                      animation: "spinner-move 2s linear infinite, spinner-dash 4s ease-in-out infinite",
                    }}
                  />
                </svg>
              </span>
            </>
          ) : isIconOnly ? (
            <span>{label}</span>
          ) : (
            <>
              {LeadingIcon && (
                <LeadingIcon
                  size={iconSize}
                  strokeWidth={1.5}
                  className="transition-[stroke-width] duration-80 group-hover/btn:[stroke-width:calc(var(--icon-stroke)_+_0.25)]"
                />
              )}
              {label}
              {TrailingIcon && (
                <TrailingIcon
                  size={iconSize}
                  strokeWidth={1.5}
                  className="transition-[stroke-width] duration-80 group-hover/btn:[stroke-width:calc(var(--icon-stroke)_+_0.25)]"
                />
              )}
            </>
          )}
        </span>
      </>
    );

    return (
      <Comp
        ref={itemRef}
        className={cn(
          buttonVariants({
            variant,
            size: resolvedSize,
            iconLeft: !isIconOnly && !!LeadingIcon,
            iconRight: !isIconOnly && !!TrailingIcon,
          }),
          shape.button,
          pill && "rounded-full",
          active && (variant === "ghost" || variant === "bare") && "text-foreground",
          className
        )}
        disabled={asChildElement ? undefined : disabled || loading}
        style={style}
        {...props}
      >
        {asChildElement
          ? cloneElement(asChildElement, undefined, internals)
          : internals}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
export type { ButtonProps, ButtonSize };
